import React, { Component } from 'react';
import GoogleMap from 'google-map-react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import config from '../../config';

const googleMapsApiKey = () => {
	const apiKey = config.REACT_APP_GOOGLE_MAPS_API_KEY || '';
	return apiKey;
};

const bootstrapURLKeys = {
  key: googleMapsApiKey(),
  libraries: ['drawing', 'places', 'geometry'].join(','),
}

// Map helper functions

let fenceSortFunction = (a, b) => {
  if (a._id === b._id) return 0;
  if (a._id < b._id) return -1;
  return 1;
};

const diffFences = (oldFencesSorted, nextFencesSorted) => {
  let removefenceIds = [];
  let addFenceIds = [];

  nextFencesSorted.sort(fenceSortFunction);
  oldFencesSorted.sort(fenceSortFunction);

  let i = 0;
  let j = 0;

  while (i < nextFencesSorted.length || j < oldFencesSorted.length) {
    if (j >= oldFencesSorted.length) {
      // keep adding new ids
      addFenceIds.push(nextFencesSorted[i]);
      i += 1;
      continue;
    }
    if (i >= nextFencesSorted.length) {
      // keep remove old obsolete ids
      removefenceIds.push(oldFencesSorted[j]);
      j += 1;
      continue;
    }

    if (nextFencesSorted[i]._id === oldFencesSorted[j]._id) {
      i += 1;
      j += 1;
      continue;
    }
    if (nextFencesSorted[i]._id < oldFencesSorted[j]._id) {
      addFenceIds.push(nextFencesSorted[i]);
      i += 1;
      continue;
    }
    if (nextFencesSorted[i]._id > oldFencesSorted[j]._id) {
      removefenceIds.push(oldFencesSorted[j]);
      j += 1;
      continue;
    }
  }
  return {
    removefenceIds,
    addFenceIds,
  }
}

const fillColor = (accuracy) => {
  switch (accuracy){
    case 1:
      return '#32ABE1';
    case 2:
      return '#2DE6D5';
    case 3:
      return '#9013FE';
    default:
      return '#32ABE1';
  }
}

function getGeoJSONPolygonFromOverlay(overlay){
  const geoJSON = {
    type: 'Polygon',
    coordinates: [],
  }
  const rawCoordinates = overlay.getPath().getArray();
  const coordinates = [];
  for (let i = 0; i < rawCoordinates.length; i += 1) {
    coordinates.push([
      rawCoordinates[i].lng(),
      rawCoordinates[i].lat(),
    ]);
  }
  if (coordinates.length > 0) coordinates.push(coordinates[0]);
  
  geoJSON.coordinates.push(coordinates);
  return geoJSON;
}

function createMapOptions(maps) {
  return {
    zoomControlOptions: {
      position: maps.ControlPosition.RIGHT_CENTER,
      style: maps.ZoomControlStyle.SMALL
    },
    mapTypeControlOptions: {
      position: maps.ControlPosition.TOP_RIGHT,
    },
    streetViewControl: true,
    streetViewControlOptions: {
        position: maps.ControlPosition.RIGHT_BOTTOM,
    },
    mapTypeControl: true,
    mapTypeId: 'hybrid',
    styles: [
      {
          featureType: "poi",
          elementType: "labels",
          stylers: [
                { visibility: "on" },
          ],
      },
    ],
  };
}

export default class Map extends Component {
  constructor(props) {
    super(props);
    this.google = null;
    this.drawingManager = null;
    this.state = {
      googleMapFences: {},
      googleMapPois: {},
      newlyDrawnFences: [],
      infoWindow: null,
    }
  }

  generateGoogleMarker(poi){
    const markerOptions = {
      position: {
        lng: poi.location.coordinates[0],
        lat: poi.location.coordinates[1],
      },
      zIndex: 9999,
      draggable: typeof poi.draggable !== 'undefined' ? poi.draggable : this.props.draggableMarkers,
      title: `<b>${poi.name}</b> - ${poi.formattedAddress}`,
    };
    if (poi.icon) {
      markerOptions.icon = poi.icon;
    }
    const newMarker = new this.google.maps.Marker(markerOptions);
    newMarker.addListener('click', () => {
      this.state.infoWindow.setContent(newMarker.title);
      this.state.infoWindow.open(this.map, newMarker);
    });
    newMarker.addListener('dragend', () => {
      if (_.isFunction(this.props.onMarkerChange)) {
        this.props.onMarkerChange(poi._id, {
          type: 'Point',
          coordinates: [newMarker.position.lng(), newMarker.position.lat()]
        }); 
      }
    });
    return newMarker;
  }

  renderSize(shape) {
    const area = window.google.maps.geometry.spherical.computeArea(shape.getPath())
    const output = area.toFixed(2) + " sqm"
    this.state.infoWindow.setContent(output);
    this.state.infoWindow.setPosition(shape.getPath().getAt(0));
    this.state.infoWindow.open(this.google.map);
  }

  generateGoogleFence(fence, editMode, color){
    if (fence.geo.type === 'Point') {
        const newCircle = new this.google.maps.Circle({
          strokeColor: '#FF0000',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: '#FF0000',
          fillOpacity: 0.35,
          center: {
            lng: fence.geo.coordinates[0],
            lat: fence.geo.coordinates[1],
          },
          radius: fence.radius,
          editable: editMode,
          draggable: false,
        });
        return newCircle;
    }
    if (fence.geo.type === 'Polygon') {
      const path = fence.geo.coordinates[0].map((c) => {
        return {
          lng: c[0],
          lat: c[1],
        }
      });

      var newPolygon = new this.google.maps.Polygon({
        paths: path,
        strokeColor: color,
        strokeOpacity: 0.6,
        strokeWeight: 2,
        fillColor: color,
        fillOpacity: 0.2,
        title: "Test",
        editable: editMode,
        draggable: false,
      });
      this.renderSize(newPolygon)

      // Edit Fence shapes event listeners
      const paths = newPolygon.getPath();
      this.google.maps.event.addListener(paths, 'insert_at', () => {
        if (_.isFunction(this.props.onFenceUpdate)) {
          this.props.onFenceUpdate();
        }
      });

      this.google.maps.event.addListener(paths, 'set_at', () => {
        if (_.isFunction(this.props.onFenceUpdate)) {
          const geoJSON = getGeoJSONPolygonFromOverlay(newPolygon);
          this.props.onFenceUpdate(geoJSON);
          this.renderSize(newPolygon)
        }
      });

      this.google.maps.event.addListener(newPolygon, 'dragstart', () => {
        if (_.isFunction(this.props.onFenceUpdate)) {
          const geoJSON = getGeoJSONPolygonFromOverlay(newPolygon);
          this.props.onFenceUpdate(geoJSON);
        }
      });
      return newPolygon;
    }
    return null;
  }
  drawFences(fenceDiff, editMode) {
    let newGoogleMapFences = { ...this.state.googleMapFences };

    // DRAW FENCES RETRIEVED FROM THE SERVER
    for (let i = 0; i < fenceDiff.addFenceIds.length; i += 1) {
      const cur = fenceDiff.addFenceIds[i];
      let color = fillColor(cur.accuracy);

      var newFence = this.generateGoogleFence(cur, editMode, cur.color ? cur.color : color);
      newFence.setMap(this.google.map);
      newGoogleMapFences[cur._id] = newFence;
    }

    // REMOVE OLD FENCES
    for (let i = 0; i < fenceDiff.removefenceIds.length; i += 1) {
      const cur = fenceDiff.removefenceIds[i];

      if (!_.isUndefined(newGoogleMapFences[cur._id]) && _.isFunction(newGoogleMapFences[cur._id].setMap)) {
        newGoogleMapFences[cur._id].setMap(null);
        delete newGoogleMapFences[cur._id];
      }
    }

    this.setState({
      googleMapFences: newGoogleMapFences,
    });
  }

  updateFenceEditMode(editMode){
    let googleMapFences = { ...this.state.googleMapFences };

    for (let i = 0; i < googleMapFences.length; i += 1){
      googleMapFences[i].setEditable(editMode);
    }
  }

  drawPois(poiDiff){
    let newGoogleMapPois = { ...this.state.googleMapPois };
    
    // DRAW FENCES RETRIEVED FROM THE SERVER
    for (let i = 0; i < poiDiff.addFenceIds.length; i += 1) {
      const cur = poiDiff.addFenceIds[i];

      switch (cur.location.type) {
        case 'Point':
          var newMarker = this.generateGoogleMarker(cur);
          newMarker.setMap(this.google.map);
          newGoogleMapPois[cur._id] = newMarker;
          break;
        default:
          continue;
      }
    }

    // REMOVE OLD FENCES
    for (let i = 0; i < poiDiff.removefenceIds.length; i += 1) {
      const cur = poiDiff.removefenceIds[i];
      if (!_.isUndefined(newGoogleMapPois[cur._id]) && _.isFunction(newGoogleMapPois[cur._id].setMap)) {
        newGoogleMapPois[cur._id].setMap(null);
        delete newGoogleMapPois[cur._id];
      }
    }

    this.setState({
      googleMapPois: newGoogleMapPois,
    });
  }

  componentWillUnmount() {
    if (_.isFunction(this.props.onWillUnmount)) {
      this.props.onWillUnmount();
    }
  }

  handleUpdateInput(value){
    if (_.isFunction(this.props.updateSearchTerm)) {
      this.props.updateSearchTerm(value);
    }
  }

  setMapBounds(data){
    if (_.isFunction(this.props.setMapBounds)){
      this.props.setMapBounds(data);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (_.isUndefined(this.google) || this.google === null || _.isUndefined(this.google.maps)) return;    

    let nextFencesSorted = nextProps.fences;
    let oldFencesSorted = this.props.fences;

    let nextPoisSorted = [];
    let oldPoisSorted = [];

    if (!_.isUndefined(nextProps.pois)){
      nextPoisSorted = nextProps.pois;
    }
    
    if (!_.isUndefined(this.props.pois)){
      oldPoisSorted = this.props.pois;
    }

    const fenceDiff = diffFences(oldFencesSorted, nextFencesSorted);
    const poiDiff = diffFences(oldPoisSorted, nextPoisSorted);

    if (fenceDiff.addFenceIds.length > 0 || fenceDiff.removefenceIds.length > 0
       || poiDiff.addFenceIds.length > 0 || poiDiff.removefenceIds.length > 0) {
         for (let i = 0; i < this.state.newlyDrawnFences.length; i += 1) {
           this.state.newlyDrawnFences[i].setMap(null);
         }
         this.setState({
            newlyDrawnFences: [],
         });
    }

    this.drawFences(fenceDiff, nextProps.editMode);
    this.drawPois(poiDiff);
    this.google.map.setTilt(0);

    // Set map cetner
    if (nextProps.mapCenter !== null) {
      this.google.map.setCenter(nextProps.mapCenter);
      this.google.map.setZoom(18);
    }

    // Handle Toggling Draw Mode
    if (nextProps.drawMode) {
      this.drawingManager.setMap(this.google.map);
    } else {
      this.drawingManager.setMap(null);
    }

    // Handle Toggling Edit Mode
    if (nextProps.editMode) {
      this.updateFenceEditMode(true);
    } else {
      this.updateFenceEditMode(false);
    }
    
    // Set Map bounds
    if (!_.isUndefined(nextProps.mapBounds) 
        && !_.isNull(nextProps.mapBounds) 
        && !_.isUndefined(nextProps.mapBounds.southwest) && !_.isUndefined(nextProps.mapBounds.northeast)) {
      const newSW = new this.google.maps.LatLng(nextProps.mapBounds.southwest);
      const newNE = new this.google.maps.LatLng(nextProps.mapBounds.northeast);
      const newBounds = new this.google.maps.LatLngBounds(newSW, newNE);
      
      this.google.map.setTilt(0);
      this.google.map.setZoom(18);
      this.google.map.panToBounds(newBounds);
    }
  }

  handleGoogleMapApi(google) {
    this.google = google;
    const map = google.map
    const self = this;

    // Setup Drawing Manager
    const drawingManager = new google.maps.drawing.DrawingManager({
      drawingMode: google.maps.drawing.OverlayType.POLYGON,
      drawingControl: false,
      drawingControlOptions: {
        position: google.maps.ControlPosition.TOP_RIGHT,
        drawingModes: [],
      },
      circleOptions: {
        fillColor: '#50E3C2',
        fillOpacity: 0.8,
        strokeWeight: 3,
        clickable: false,
        editable: false,
        zIndex: 1
      },
      polygonOptions: {
        fillColor: '#FF0221',
        editable: this.props.editMode,
      },
    });

    this.drawingManager = drawingManager;
    if (self.props.drawMode) {
      drawingManager.setMap(map);
    }

    // Drawing Manager callbacks
    google.maps.event.addListener(drawingManager, 'overlaycomplete', function (event) {
      let geoJSON = null;
      if (event.type === 'polygon') {
        geoJSON = getGeoJSONPolygonFromOverlay(event.overlay);
      }
      if (event.type === 'circle') {
        geoJSON = {
          type: 'Point',
          coordinates: [
            event.overlay.center.lng(),
            event.overlay.center.lat(),
          ]
        }
      }
      self.drawingManager = drawingManager;
      self.props.newFenceDrawn({ geometry: geoJSON });

      self.setState({
        newlyDrawnFences: [...self.state.newlyDrawnFences, event.overlay],
      });
    });

    // Disable tilt on zoom
    map.setTilt(0);

    // Info window

    var infoWindow = new google.maps.InfoWindow({
      content: '',
      maxWidth: 300,
    });

    this.setState({
      infoWindow,
    });

    // If the center is set, set map center

    if (this.props.mapCenter !== null) {
      if (_.isFunction(map.setCenter)) map.setCenter(this.props.mapCenter);
      if (_.isFunction(map.setZoom)) map.setZoom(20);
    }

    // If the map bounds are set, set map bounds

    if (!_.isUndefined(this.props.mapBounds) 
        && !_.isNull(this.props.mapBounds) 
        && !_.isUndefined(this.props.mapBounds.southwest) && !_.isUndefined(this.props.mapBounds.northeast)) {
      const newSW = new google.maps.LatLng(this.props.mapBounds.southwest);
      const newNE = new google.maps.LatLng(this.props.mapBounds.northeast);
      const newBounds = new google.maps.LatLngBounds(newSW, newNE);

      if (_.isFunction(map.setZoom)) map.setZoom(18);
      if (_.isFunction(map.setMapBounds)) map.setMapBounds(newBounds);
    }

    // Draw fences and POIs
    if (!_.isUndefined(this.google) && !_.isUndefined(this.google.maps)) {
      let fencesSorted = this.props.fences;
      let poisSorted = this.props.pois;

      const fenceDiff = diffFences([], fencesSorted);
      const poiDiff = diffFences([], poisSorted);

      this.drawFences(fenceDiff, self.props.editMode);
      this.drawPois(poiDiff);
    }

    // Set Event Listeners
    map.addListener('center_changed', () => {
      if (_.isFunction(self.props.centerChanged)) self.props.centerChanged(null);
      if (_.isFunction(self.props.setMapBounds)) self.props.setMapBounds(null);
    });
    map.addListener('bounds_changed', () => {
      const bounds = map.getBounds().toJSON();
      const boundsFormatted = {
        topLeft: [bounds.west, bounds.north],
        bottomRight: [bounds.east, bounds.south],
      };
      if (_.isFunction(this.props.viewpointChanged)) {
        this.props.viewpointChanged(boundsFormatted.topLeft, boundsFormatted.bottomRight);
      }
    });
  }

  render() {
    return (
      <div style={{ width: '100%', height: '100%', position: 'relative'}}>
        <GoogleMap
          center={{ lat: -34.397, lng: 150.644 }}
          defaultZoom={8}
          bootstrapURLKeys={bootstrapURLKeys}
          options={createMapOptions}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={this.handleGoogleMapApi.bind(this)}>
        </GoogleMap>
      </div>
    );
  }
}
//React.PropTypes.func,

Map.propTypes = {
  drawMode: PropTypes.bool,
  editMode: PropTypes.bool,
  
  pois: PropTypes.array,
  markers: PropTypes.array,
  fences: PropTypes.array,
  drawOnlyTopFence: PropTypes.bool,
  draggableMarkers: PropTypes.bool,
  showGeocode: PropTypes.bool,
  
  mapBounds: PropTypes.shape({
    southwest: PropTypes.object,
    northeast: PropTypes.object,
  }),

  mapCenter: PropTypes.object,

  viewpointChanged: PropTypes.func,
  setMapBounds: PropTypes.func,
  onWillUnmount: PropTypes.func,
  updateSearchTerm: PropTypes.func,
  onFenceUpdate: PropTypes.func,
  onMarkerChange: PropTypes.func,
}

Map.defaultProps = {
  drawMode: true,
  editMode: false,
  pois: [],
  markers: [],
  fences: [],
  showGeocode: true,

  drawOnlyTopFence: true,
  draggableMarkers: false,
}



// WEBPACK FOOTER //
// ./src/shared/components/Map.js